import React from "react";
import { interpolate, spring, useVideoConfig } from "remotion";
import { BulletItem } from "../../types";
import { SvgIcon } from "../../components/SvgComponents";
import { CompanyBadge } from "../../components/CompanyBadge";
import { detectBrandInText } from "../../components/companyDetector";


interface GridMotionInsightsProps {
  bullets: BulletItem[];
  globalFrame: number;
}

const SlideContent: React.FC<{
  bullet: BulletItem;
  elapsedFrames: number;
  translateX: number;
  opacity: number;
  activeIndex: number;
  totalBullets: number;
  fps: number;
}> = ({ bullet, elapsedFrames, translateX, opacity, activeIndex, totalBullets, fps }) => {
  // SVG scanner scale entrance
  const svgScale = spring({
    frame: elapsedFrames,
    fps,
    from: 0.3,
    to: 1.0,
    config: { damping: 13, mass: 0.5, stiffness: 120 },
  });

  const floatY = Math.sin(elapsedFrames * 0.05) * 10;

  // Staggered word animation
  const words = bullet.text.split(" ");
  
  const isHighlighted = (word: string, highlights?: string[]) => {
    if (!highlights) return false;
    const cleanWord = word.replace(/[.,/#!$%^&*;:{}=\-_`~()?"']/g, "").toLowerCase();
    return highlights.some(
      (h) => cleanWord.includes(h.toLowerCase()) || h.toLowerCase().includes(cleanWord)
    );
  };

  const highlightStyle: React.CSSProperties = {
    fontWeight: 900,
    background: "linear-gradient(135deg, #bef264 30%, #00f2fe 100%)", // Lime to Cyan gradient
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    textShadow: "0 0 15px rgba(190, 242, 100, 0.4)",
    display: "inline-block",
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0 0 240px 0",
        boxSizing: "border-box",
        transform: `translateX(${translateX}px)`,
        opacity,
      }}
    >
      {/* 1. Cybernetic SVG Scanner Box */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          flex: 1,
          maxHeight: "45%",
          position: "relative",
          transform: `scale(${svgScale}) translateY(${floatY}px)`,
          zIndex: 2,
        }}
      >
        {/* Outer rotating scanner ring (Cyan) */}
        <div
          style={{
            position: "absolute",
            width: "360px",
            height: "360px",
            border: "2px dashed rgba(0, 242, 254, 0.35)",
            borderRadius: "50%",
            transform: `rotate(${elapsedFrames * 0.9}deg)`,
            boxShadow: "0 0 20px rgba(0, 242, 254, 0.1)",
          }}
        />

        {/* Inner rotating scanner ring (Magenta, counter-clockwise) */}
        <div
          style={{
            position: "absolute",
            width: "320px",
            height: "320px",
            border: "1.5px dashed rgba(255, 0, 127, 0.3)",
            borderRadius: "50%",
            transform: `rotate(${-elapsedFrames * 1.5}deg)`,
          }}
        />

        {/* Static crosshair marks */}
        <div style={{ position: "absolute", top: "50%", left: "50%", width: "380px", height: "1px", background: "rgba(255, 255, 255, 0.05)", transform: "translate(-50%, -50%)" }} />
        <div style={{ position: "absolute", top: "50%", left: "50%", height: "380px", width: "1px", background: "rgba(255, 255, 255, 0.05)", transform: "translate(-50%, -50%)" }} />

        {/* Neon HUD arc overlay */}
        <svg
          style={{
            position: "absolute",
            width: "400px",
            height: "400px",
            transform: `rotate(${elapsedFrames * 0.5}deg)`,
            pointerEvents: "none",
          }}
          viewBox="0 0 100 100"
        >
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#bef264"
            strokeWidth="1"
            strokeDasharray="15 120"
            opacity="0.6"
          />
          <circle
            cx="50"
            cy="50"
            r="46"
            fill="none"
            stroke="#ff007f"
            strokeWidth="1.5"
            strokeDasharray="25 150"
            strokeDashoffset="70"
            opacity="0.5"
          />
        </svg>

        {/* Core SVG */}
        <div
          style={{
            zIndex: 5,
            filter: "drop-shadow(0 15px 30px rgba(0, 242, 254, 0.3))",
          }}
        >
          {(() => {
            const brand = detectBrandInText(bullet.text);
            return brand ? (
              <CompanyBadge
                name={brand}
                color="#00f2fe"
                accentColor="#00f2fe"
                size={180}
                style="glow"
              />
            ) : (
              <SvgIcon
                name={bullet.icon || "undraw-astronaut-0o7w"}
                width={280}
                height={280}
                primaryColor="#00f2fe"
              />
            );
          })()}
        </div>
      </div>

      {/* 2. Glassmorphic HUD panel containing big typography */}
      <div
        style={{
          width: "100%",
          padding: "0 45px",
          boxSizing: "border-box",
          zIndex: 3,
        }}
      >
        <div
          style={{
            background: "rgba(4, 5, 15, 0.75)",
            backdropFilter: "blur(20px)",
            border: "1.5px solid rgba(0, 242, 254, 0.25)",
            borderRadius: "20px",
            padding: "36px 30px",
            boxShadow: "0 20px 40px rgba(0,0,0,0.5), 0 0 25px rgba(0, 242, 254, 0.1)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Neon Corner Tech Brackets on the card */}
          <div style={{ position: "absolute", top: 0, left: 0, width: "12px", height: "12px", borderLeft: "2px solid #bef264", borderTop: "2px solid #bef264" }} />
          <div style={{ position: "absolute", top: 0, right: 0, width: "12px", height: "12px", borderRight: "2px solid #bef264", borderTop: "2px solid #bef264" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, width: "12px", height: "12px", borderLeft: "2px solid #bef264", borderBottom: "2px solid #bef264" }} />
          <div style={{ position: "absolute", bottom: 0, right: 0, width: "12px", height: "12px", borderRight: "2px solid #bef264", borderBottom: "2px solid #bef264" }} />

          {/* Staggered word animation */}
          <p
            style={{
              fontFamily: "var(--font-title)",
              fontSize: "38px",
              fontWeight: 700,
              color: "#ffffff",
              lineHeight: 1.35,
              textAlign: "left",
              margin: 0,
            }}
          >
            {words.map((word, idx) => {
              // Calculate staggered frame delay
              const delay = idx * 1.5;
              const wordFrame = Math.max(0, elapsedFrames - delay);

              // Spring pop and scale
              const wordScale = spring({
                frame: wordFrame,
                fps,
                from: 0.0,
                to: 1.0,
                config: { damping: 10, stiffness: 140 }, // Bouncy kinetic spring
              });

              // Slide in from alternating left/right directions
              const fromX = idx % 2 === 0 ? -40 : 40;
              const wordTranslateX = interpolate(wordScale, [0, 1], [fromX, 0]);
              
              const wordOpacity = interpolate(wordFrame, [0, 6], [0, 1], {
                extrapolateLeft: "clamp",
                extrapolateRight: "clamp",
              });

              return (
                <span
                  key={idx}
                  style={{
                    display: "inline-block",
                    marginRight: "12px",
                    transform: `scale(${wordScale}) translateX(${wordTranslateX}px)`,
                    opacity: wordOpacity,
                  }}
                >
                  {isHighlighted(word, bullet.highlights) ? (
                    <span style={highlightStyle}>{word}</span>
                  ) : (
                    <span>{word}</span>
                  )}
                </span>
              );
            })}
          </p>

          {/* Card bottom progress indicator line */}
          <div
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              height: "4px",
              width: `${((activeIndex + 1) / totalBullets) * 100}%`,
              background: "linear-gradient(90deg, #bef264, #00f2fe)",
              boxShadow: "0 -2px 10px rgba(190, 242, 100, 0.4)",
            }}
          />
        </div>
      </div>
    </div>
  );
};

export const GridMotionInsights: React.FC<GridMotionInsightsProps> = ({
  bullets,
  globalFrame,
}) => {
  const { fps, width } = useVideoConfig();
  const currentMs = (globalFrame / fps) * 1000;

  // Identify which bullet insight is active
  let activeIndex = bullets.findIndex(
    (b) => currentMs >= b.startMs && currentMs <= b.endMs
  );

  if (activeIndex === -1) {
    const pastBullets = bullets.filter((b) => b.endMs <= currentMs);
    activeIndex = pastBullets.length > 0 ? pastBullets.length - 1 : 0;
  }

  const activeBullet = bullets[activeIndex];
  const bulletStartFrame = (activeBullet.startMs / 1000) * fps;
  const elapsedFrames = Math.max(0, globalFrame - bulletStartFrame);

  // Slide transition window
  const transitionFrames = 12;
  const isInTransition = elapsedFrames < transitionFrames && activeIndex > 0;

  let currentSlideX = 0;
  let currentSlideOpacity = 1;

  if (isInTransition) {
    const progress = elapsedFrames / transitionFrames;
    currentSlideX = width * (1 - progress);
    currentSlideOpacity = progress;
  }

  // Neon wipe sweep overlay coordinates
  const sweepProgress = interpolate(elapsedFrames, [0, transitionFrames], [0, 100]);
  const sweepX = interpolate(sweepProgress, [0, 100], [-130, 130]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        flex: 1,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Slide Pagination Dots */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          paddingTop: "24px",
          zIndex: 10,
        }}
      >
        {bullets.map((_, idx) => (
          <div
            key={idx}
            style={{
              width: idx === activeIndex ? 32 : 10,
              height: 10,
              borderRadius: 5,
              background:
                idx === activeIndex
                  ? "linear-gradient(90deg, #bef264, #00f2fe)"
                  : "rgba(255, 255, 255, 0.2)",
              boxShadow:
                idx === activeIndex ? "0 0 10px rgba(190, 242, 100, 0.4)" : "none",
            }}
          />
        ))}
      </div>

      {/* Slide contents */}
      <div style={{ flex: 1, position: "relative", width: "100%" }}>
        {/* Outgoing slide rendering */}
        {isInTransition && (
          <SlideContent
            key={activeIndex - 1}
            bullet={bullets[activeIndex - 1]}
            elapsedFrames={elapsedFrames + (bullets[activeIndex].startMs - bullets[activeIndex - 1].startMs) / 1000 * fps}
            translateX={-width * (elapsedFrames / transitionFrames)}
            opacity={1 - elapsedFrames / transitionFrames}
            activeIndex={activeIndex - 1}
            totalBullets={bullets.length}
            fps={fps}
          />
        )}

        {/* Incoming/Active slide rendering */}
        <SlideContent
          key={activeIndex}
          bullet={activeBullet}
          elapsedFrames={elapsedFrames}
          translateX={currentSlideX}
          opacity={currentSlideOpacity}
          activeIndex={activeIndex}
          totalBullets={bullets.length}
          fps={fps}
        />

        {/* Diagonal Neon sweep transition shutter */}
        {isInTransition && (
          <div
            style={{
              position: "absolute",
              top: -150,
              bottom: -150,
              width: "60%",
              background: "linear-gradient(90deg, transparent, rgba(190, 242, 100, 0.5), rgba(0, 242, 254, 0.5), transparent)",
              transform: `skewX(-25deg) translateX(${sweepX}%)`,
              pointerEvents: "none",
              zIndex: 20,
              boxShadow: "0 0 50px rgba(190, 242, 100, 0.3)",
            }}
          />
        )}
      </div>
    </div>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
